/**
 * API Documentation Page
 *
 * Comprehensive REST API reference with interactive examples,
 * code snippets, and endpoint descriptions.
 */
import { useState } from 'react'
import {
  Globe,
  Zap,
  Shield,
  Clock,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Terminal,
  Code2,
  Binary,
  BarChart3,
  Sliders,
  TrendingUp,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const BASE_URL = 'https://bitassay.com'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

function CodeBlock({ code, lang = 'bash' }: { code: string; lang?: string }) {
  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 overflow-x-auto text-sm leading-relaxed [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <code className={`language-${lang}`}>{code.trim()}</code>
      </pre>
      <CopyButton text={code.trim()} />
    </div>
  )
}

function Collapsible({
  title,
  icon: Icon,
  method,
  path,
  children,
  defaultOpen = false,
}: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  method: string
  path: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const methodColor =
    method === 'GET'
      ? 'bg-green-500/15 text-green-600 dark:text-green-400'
      : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'

  return (
    <Card className="border border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 sm:px-6 py-4 flex items-center gap-3 hover:bg-accent/50 transition-colors rounded-t-xl"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <Icon className="h-5 w-5 text-primary shrink-0" />
        <span className="font-semibold text-sm sm:text-base">{title}</span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <span className={cn('px-2 py-0.5 rounded text-xs font-bold', methodColor)}>{method}</span>
          <code className="text-xs text-muted-foreground font-mono hidden sm:inline">{path}</code>
        </div>
      </button>
      {open && (
        <CardContent className="px-4 sm:px-6 pb-6 pt-0 space-y-4 border-t border-border">
          {children}
        </CardContent>
      )}
    </Card>
  )
}

function ParamRow({
  name,
  type,
  required,
  def,
  desc,
}: {
  name: string
  type: string
  required?: boolean
  def?: string
  desc: string
}) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pr-3">
        <code className="text-xs font-mono text-primary">{name}</code>
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </td>
      <td className="py-2 pr-3 text-xs text-muted-foreground">{type}</td>
      <td className="py-2 pr-3 text-xs font-mono text-muted-foreground">{def ?? '—'}</td>
      <td className="py-2 text-xs">{desc}</td>
    </tr>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function APIDocsPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 overflow-hidden">
      {/* Hero */}
      <div className="text-center mb-10 sm:mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
          <Globe className="h-4 w-4" />
          REST API
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">BitAssay API</h1>
        <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
          Run the full suite of statistical tests programmatically.
          Open access, no API key required.
        </p>
        <div className="mt-4 inline-block">
          <code className="px-3 py-1.5 rounded-md bg-muted text-sm font-mono">
            {BASE_URL}/api/
          </code>
        </div>
      </div>

      {/* Quick features */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <Zap className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">No Auth Required</p>
              <p className="text-xs text-muted-foreground mt-0.5">Fully open — just send a request</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <Shield className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">CORS Enabled</p>
              <p className="text-xs text-muted-foreground mt-0.5">Call from any origin, including browsers</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Rate Limited</p>
              <p className="text-xs text-muted-foreground mt-0.5">30 requests / minute per IP</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Privacy note */}
      <div className="mb-10 p-4 rounded-lg border border-blue-500/20 bg-blue-500/5">
        <p className="text-sm">
          <strong className="text-blue-600 dark:text-blue-400">Privacy note:</strong>{' '}
          The web interface processes all data locally in your browser &mdash; nothing is uploaded.
          The REST API, by contrast, runs on Cloudflare edge servers via serverless cloud functions.
          These functions are <strong>purely stateless</strong>: your data is used only for the
          duration of the computation and is <strong>never stored, logged, or retained</strong> in any form.
        </p>
      </div>

      {/* Quick start */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Terminal className="h-5 w-5" />
          Quick Start
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          Test the API with a simple curl command:
        </p>
        <CodeBlock
          code={`curl ${BASE_URL}/api/health`}
        />
        <p className="text-sm text-muted-foreground mt-4 mb-3">
          Run SP 800-22 tests on a binary string:
        </p>
        <CodeBlock
          lang="bash"
          code={`curl -X POST ${BASE_URL}/api/sp800-22 \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": "01101001011010010110100101101001011010010110100101101001011010010110100101101001011010010110100101101001",
    "params": { "alpha": 0.01 }
  }'`}
        />
      </div>

      {/* Data formats */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Code2 className="h-5 w-5" />
          Data Formats
        </h2>
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <p className="text-sm">
              The <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">data</code> field
              accepts three formats for binary modules (SP 800-22, SP 800-90B, Tunable-Bias):
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-2 pr-4 font-medium">Format</th>
                    <th className="py-2 pr-4 font-medium">Example</th>
                    <th className="py-2 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono text-xs">Binary string</td>
                    <td className="py-2 pr-4 font-mono text-xs">"01101001..."</td>
                    <td className="py-2 text-xs">String of 0s and 1s</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono text-xs">Base64</td>
                    <td className="py-2 pr-4 font-mono text-xs">"aWlpaQ=="</td>
                    <td className="py-2 text-xs">Base64-encoded binary data</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">Number array</td>
                    <td className="py-2 pr-4 font-mono text-xs">[0,1,1,0,...]</td>
                    <td className="py-2 text-xs">Array of 0s and 1s</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm">
              The <strong>Gaussian</strong> module accepts only a <strong>number array</strong> of
              continuous floating-point samples:
            </p>
            <CodeBlock
              lang="json"
              code={`{ "data": [1.23, -0.45, 2.01, 0.78, -1.34, ...] }`}
            />
          </CardContent>
        </Card>
      </div>

      {/* Endpoints */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Endpoints</h2>
        <div className="space-y-3">
          {/* Health */}
          <Collapsible
            title="Health Check"
            icon={Globe}
            method="GET"
            path="/api/health"
            defaultOpen
          >
            <p className="text-sm text-muted-foreground">
              Returns API status, version, and available modules.
            </p>
            <CodeBlock code={`curl ${BASE_URL}/api/health`} />
            <p className="text-xs font-medium text-muted-foreground mt-2">Response:</p>
            <CodeBlock
              lang="json"
              code={`{
  "status": "ok",
  "version": "1.2.0",
  "modules": ["sp800-22", "sp800-90b", "tunable-bias", "gaussian"],
  "limits": {
    "maxInputSize": 10000000,
    "rateLimit": "30 requests/minute per IP"
  },
  "docs": "https://bitassay.com/api-docs"
}`}
            />
          </Collapsible>

          {/* SP 800-22 */}
          <Collapsible title="SP 800-22 — NIST Statistical Tests" icon={Binary} method="POST" path="/api/sp800-22">
            <p className="text-sm text-muted-foreground">
              Run the complete 15-test NIST SP 800-22 statistical test suite on binary data.
            </p>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 text-xs font-medium">Name</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Type</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Default</th>
                    <th className="py-1.5 text-xs font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <ParamRow name="data" type="string | number[]" required desc="Binary data (see Data Formats)" />
                  <ParamRow name="params.alpha" type="number" def="0.01" desc="Significance level" />
                  <ParamRow name="params.blockSizeFreq" type="number" def="128" desc="Block frequency block size M" />
                  <ParamRow name="params.templateLength" type="number" def="9" desc="Non-overlapping template length m" />
                  <ParamRow name="params.blockSizeLinear" type="number" def="500" desc="Linear complexity block size M" />
                  <ParamRow name="params.patternLength" type="number" def="10" desc="Serial / ApEn pattern length m" />
                </tbody>
              </table>
            </div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Example</h4>
            <CodeBlock
              lang="bash"
              code={`curl -X POST ${BASE_URL}/api/sp800-22 \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": [1,0,1,1,0,0,1,0,1,1,0,1,0,0,1,1,...],
    "params": { "alpha": 0.01 }
  }'`}
            />
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Response (abbreviated)</h4>
            <CodeBlock
              lang="json"
              code={`{
  "module": "sp800-22",
  "inputBits": 1000000,
  "overallPassed": true,
  "passCount": 15,
  "failCount": 0,
  "totalTests": 15,
  "executionTimeMs": 2340,
  "tests": [
    {
      "testName": "Frequency (Monobit)",
      "testId": 1,
      "pValues": [0.534146],
      "passed": [true],
      "statistic": 0.6179,
      "overallPassed": true,
      "executionTimeMs": 12
    },
    ...
  ]
}`}
            />
          </Collapsible>

          {/* SP 800-90B */}
          <Collapsible title="SP 800-90B — Entropy Estimation" icon={BarChart3} method="POST" path="/api/sp800-90b">
            <p className="text-sm text-muted-foreground">
              Run SP 800-90B entropy source evaluation including IID testing and min-entropy estimation.
            </p>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 text-xs font-medium">Name</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Type</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Default</th>
                    <th className="py-1.5 text-xs font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <ParamRow name="data" type="string | number[]" required desc="Binary data" />
                  <ParamRow name="symbolSize" type="number" def="1" desc="Bits per symbol (1-8)" />
                  <ParamRow name="params.numPermutations" type="number" def="1000" desc="IID permutations (100-5000)" />
                  <ParamRow name="params.verboseIID" type="boolean" def="false" desc="Include detailed IID stats" />
                </tbody>
              </table>
            </div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Example</h4>
            <CodeBlock
              lang="bash"
              code={`curl -X POST ${BASE_URL}/api/sp800-90b \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": "0110100101101001...",
    "symbolSize": 1,
    "params": { "numPermutations": 1000 }
  }'`}
            />
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Response (abbreviated)</h4>
            <CodeBlock
              lang="json"
              code={`{
  "module": "sp800-90b",
  "inputBits": 100000,
  "symbolSize": 1,
  "numSamples": 100000,
  "iidTestResults": {
    "iidConclusion": true,
    "numPermutations": 1000,
    "statistics": [...]
  },
  "entropyEstimates": [
    { "estimatorName": "Most Common Value", "minEntropy": 0.9987 },
    ...
  ],
  "finalMinEntropy": 0.9543,
  "executionTimeMs": 8200
}`}
            />
            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-xs text-yellow-700 dark:text-yellow-300">
              <strong>Note:</strong> SP 800-90B with many permutations can take 10-20 seconds. The API limits
              permutations to 5,000 (vs 10,000 in the browser UI) to stay within server execution time limits.
            </div>
          </Collapsible>

          {/* Tunable-Bias */}
          <Collapsible title="Tunable-Bias — Biased RNG Analysis" icon={Sliders} method="POST" path="/api/tunable-bias">
            <p className="text-sm text-muted-foreground">
              Run the novel 9-test framework for evaluating non-uniform (biased) random number generators.
            </p>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 text-xs font-medium">Name</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Type</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Default</th>
                    <th className="py-1.5 text-xs font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <ParamRow name="data" type="string | number[]" required desc="Binary data" />
                  <ParamRow name="params.targetBias" type="number" def="0.5" desc="Expected P(1) = p₀ (0.001-0.999)" />
                  <ParamRow name="params.alpha" type="number" def="0.01" desc="Significance level" />
                  <ParamRow name="params.stabilityBlocks" type="number" def="100" desc="Number of blocks K (10-1000)" />
                  <ParamRow name="params.maxLag" type="number" def="1000" desc="ACF max lag" />
                  <ParamRow name="params.serialPatternLength" type="number" def="8" desc="Serial pattern length m" />
                  <ParamRow name="params.maxConditionalOrder" type="number" def="10" desc="Max conditional entropy order" />
                </tbody>
              </table>
            </div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Example</h4>
            <CodeBlock
              lang="bash"
              code={`curl -X POST ${BASE_URL}/api/tunable-bias \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": "1110110111011011...",
    "params": { "targetBias": 0.7, "alpha": 0.01 }
  }'`}
            />
          </Collapsible>

          {/* Gaussian */}
          <Collapsible title="Gaussian — Distribution Validation" icon={TrendingUp} method="POST" path="/api/gaussian">
            <p className="text-sm text-muted-foreground">
              Run 4 normality tests (Shapiro-Wilk, Anderson-Darling, D'Agostino-Pearson, KS) on continuous data.
            </p>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 text-xs font-medium">Name</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Type</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Default</th>
                    <th className="py-1.5 text-xs font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <ParamRow name="data" type="number[]" required desc="Continuous floating-point samples" />
                  <ParamRow name="params.alpha" type="number" def="0.05" desc="Significance level" />
                  <ParamRow name="params.useEstimated" type="boolean" def="true" desc="Estimate μ and σ from data" />
                  <ParamRow name="params.mu" type="number" def="—" desc="Known mean (if useEstimated=false)" />
                  <ParamRow name="params.sigma" type="number" def="—" desc="Known std dev (if useEstimated=false)" />
                </tbody>
              </table>
            </div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-4 mb-2">Example</h4>
            <CodeBlock
              lang="bash"
              code={`curl -X POST ${BASE_URL}/api/gaussian \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": [0.12, -1.34, 0.78, 2.01, -0.45, 1.56, ...],
    "params": { "alpha": 0.05 }
  }'`}
            />
          </Collapsible>
        </div>
      </div>

      {/* Code examples */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Code2 className="h-5 w-5" />
          Code Examples
        </h2>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Python</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock
                lang="python"
                code={`import requests
import json

# Generate random bits
import secrets
bits = ''.join(str(secrets.randbelow(2)) for _ in range(100000))

# Run SP 800-22 tests
response = requests.post(
    "${BASE_URL}/api/sp800-22",
    json={
        "data": bits,
        "params": {"alpha": 0.01}
    }
)

result = response.json()
print(f"Overall: {'PASS' if result['overallPassed'] else 'FAIL'}")
print(f"Passed: {result['passCount']}/{result['totalTests']}")
for test in result['tests']:
    status = 'PASS' if test['overallPassed'] else 'FAIL'
    print(f"  [{status}] {test['testName']} (p={test['pValues'][0]:.6f})")`}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">JavaScript / Node.js</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock
                lang="javascript"
                code={`// Generate random bits
const bits = Array.from(crypto.getRandomValues(new Uint8Array(12500)))
  .flatMap(b => Array.from({length: 8}, (_, i) => (b >> (7 - i)) & 1));

// Run SP 800-22 tests
const response = await fetch("${BASE_URL}/api/sp800-22", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    data: bits,
    params: { alpha: 0.01 }
  })
});

const result = await response.json();
console.log(\`Overall: \${result.overallPassed ? 'PASS' : 'FAIL'}\`);
console.log(\`Passed: \${result.passCount}/\${result.totalTests}\`);`}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rate limiting & errors */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Rate Limiting & Errors</h2>
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <p className="text-sm">
              The API allows <strong>30 requests per minute</strong> per IP address. Rate limit status is
              returned in response headers:
            </p>
            <CodeBlock
              code={`X-RateLimit-Limit: 30
X-RateLimit-Remaining: 28
X-RateLimit-Reset: 1711382460`}
            />
            <p className="text-sm">When the limit is exceeded, you'll receive a <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">429</code> response:</p>
            <CodeBlock
              lang="json"
              code={`{
  "error": "Rate limit exceeded",
  "message": "Maximum 30 requests per minute. Try again in 42s.",
  "retryAfter": 42
}`}
            />

            <h3 className="text-sm font-semibold mt-4">Error Responses</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 text-xs font-medium">Status</th>
                    <th className="py-1.5 pr-3 text-xs font-medium">Meaning</th>
                    <th className="py-1.5 text-xs font-medium">Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-3 font-mono text-xs">400</td>
                    <td className="py-2 pr-3 text-xs">Bad request</td>
                    <td className="py-2 text-xs font-mono">Missing data, invalid format, input too small</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-3 font-mono text-xs">429</td>
                    <td className="py-2 pr-3 text-xs">Rate limited</td>
                    <td className="py-2 text-xs font-mono">Too many requests</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-3 font-mono text-xs">500</td>
                    <td className="py-2 pr-3 text-xs">Server error</td>
                    <td className="py-2 text-xs font-mono">Computation timeout or unexpected error</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Limits */}
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4">Limits</h2>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-medium text-sm">Max input size</td>
                    <td className="py-2 text-sm">10,000,000 elements (bits or samples)</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-medium text-sm">Rate limit</td>
                    <td className="py-2 text-sm">30 requests / minute per IP</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-medium text-sm">Max execution time</td>
                    <td className="py-2 text-sm">30 seconds per request</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-medium text-sm">SP 800-90B permutations</td>
                    <td className="py-2 text-sm">100 – 5,000 (default 1,000)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-sm">Response format</td>
                    <td className="py-2 text-sm">JSON (Content-Type: application/json)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
